import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  LockOutlined,
  MailOutline,
  AdminPanelSettingsOutlined,
  BusinessOutlined,
  SchoolOutlined,
} from "@mui/icons-material";
import { useState, useEffect, useRef, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { extractApiError } from "@/shared/api/apiClient";
import type { LoginResponse, SchoolCandidate } from "@/shared/api/types";
import { authApi, DEFAULT_CAPABILITIES, type AuthChannel } from "./authApi";
import { useAuth } from "@/shared/hooks/useAuth";
import { useThemeMode } from "@/app/ThemeContext";
import { logos } from "@/app/brand";
import { ThemeControls } from "@/shared/components/ThemeControls";
import { PhoneField } from "@/shared/components/PhoneField";
import { isDevLoginEnabled } from "@/shared/utils/env";
import { SchoolRegistrationRequestDialog } from "./SchoolRegistrationRequestDialog";

const PHONE_REGEX = /^[0-9]{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOCK_OTP_CODE = "123456";
// @zekabin.com test adresleri her ortamda sabit 909090 kodu alır (bkz DbBackedEmailService);
// diğer adresler dev mock kodu (123456) kullanır.
const ZEKABIN_TEST_OTP = "909090";
const quickLoginCode = (email: string) =>
  email.toLowerCase().endsWith("@zekabin.com") ? ZEKABIN_TEST_OTP : MOCK_OTP_CODE;

const DEV_USERS = [
  { label: "Admin", email: "admin@zekabin.com", icon: AdminPanelSettingsOutlined, color: "error" as const },
  { label: "Yayınevi", email: "yayinevi@zekabin.com", icon: BusinessOutlined, color: "warning" as const },
  { label: "Okul", email: "oy@zekabin.com", icon: SchoolOutlined, color: "info" as const },
];

export const LoginPage = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const { mode } = useThemeMode();

  const { data: capabilities = DEFAULT_CAPABILITIES } = useQuery({
    queryKey: ["auth-capabilities"],
    queryFn: authApi.getCapabilities,
    // Public uç; başarısız olursa varsayılan (e-posta açık, SMS kapalı) korunur.
    retry: false,
    placeholderData: DEFAULT_CAPABILITIES,
  });

  const [channel, setChannel] = useState<AuthChannel>("EMAIL");
  const [step, setStep] = useState<"identifier" | "code" | "select">("identifier");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Çoklu-okul: giriş yanıtı token yerine okul seçimi isterse.
  const [selectionToken, setSelectionToken] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<SchoolCandidate[]>([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Yönetici SMS'i kapattıysa ve kullanıcı bir şekilde SMS sekmesindeyse e-postaya düş.
  useEffect(() => {
    if (channel === "SMS" && !capabilities.smsLoginEnabled) setChannel("EMAIL");
  }, [channel, capabilities.smsLoginEnabled]);

  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus();
  }, [step]);

  const identifierValue = channel === "EMAIL" ? email : phone;

  const validateIdentifier = (): string | null => {
    if (channel === "EMAIL") {
      return EMAIL_REGEX.test(email) ? null : "Geçerli bir e-posta adresi giriniz";
    }
    return PHONE_REGEX.test(phone) ? null : "Geçerli bir telefon numarası giriniz (sadece rakam, 10 hane)";
  };

  // Giriş yanıtını uygula: token dönerse oturumu aç; okul seçimi isterse "select" adımına geç.
  const applyLoginResponse = (data: LoginResponse) => {
    if (data.accessToken && data.user) {
      setAuth(data.accessToken, data.user);
      navigate("/", { replace: true });
      return;
    }
    setSelectionToken(data.selectionToken ?? null);
    setCandidates(data.candidates ?? []);
    setError(null);
    setInfo(null);
    setStep("select");
  };

  const chooseSchool = async (recordId: string) => {
    if (!selectionToken) return;
    setError(null);
    setLoading(true);
    try {
      applyLoginResponse(await authApi.selectSchool(selectionToken, recordId));
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (loginEmail: string) => {
    setError(null);
    setLoading(true);
    try {
      await authApi.requestOtp("EMAIL", loginEmail);
      applyLoginResponse(await authApi.login("EMAIL", loginEmail, quickLoginCode(loginEmail)));
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validateIdentifier();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      await authApi.requestOtp(channel, identifierValue);
      setInfo(
        channel === "EMAIL"
          ? "E-posta adresinize doğrulama kodu gönderildi"
          : "Telefonunuza doğrulama kodu gönderildi",
      );
      setStep("code");
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const login = async (e: FormEvent, overrideCode?: string) => {
    e.preventDefault();
    const codeToSubmit = overrideCode ?? code;
    setError(null);
    if (codeToSubmit.length !== 6) {
      setError("Doğrulama kodu 6 haneli olmalıdır");
      return;
    }
    setLoading(true);
    try {
      applyLoginResponse(await authApi.login(channel, identifierValue, codeToSubmit));
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetToIdentifier = () => {
    setStep("identifier");
    setCode("");
    setError(null);
    setInfo(null);
    setSelectionToken(null);
    setCandidates([]);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        p: 2,
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 0.5 }}>
        <ThemeControls />
      </Box>

      <Card sx={{ width: 420, maxWidth: "100%" }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
            <Box
              component="img"
              src={mode === "dark" ? logos.fullDark : logos.fullLight}
              alt="Zekabin"
              sx={{ height: 58, objectFit: "contain", maxWidth: "100%" }}
            />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {step === "select"
                ? "Birden fazla okulda kaydınız var. Hangi okul adına giriş yapmak istiyorsunuz?"
                : step === "identifier"
                ? channel === "EMAIL"
                  ? "E-posta adresinizle giriş yapın. Hesabınıza kısa bir doğrulama kodu göndereceğiz."
                  : "Telefon numaranızla giriş yapın. Hesabınıza kısa bir doğrulama kodu göndereceğiz."
                : channel === "EMAIL"
                ? "E-posta adresinize gelen 6 haneli doğrulama kodunu giriniz."
                : "Telefonunuza gelen 6 haneli doğrulama kodunu giriniz."}
            </Typography>
          </Stack>

          {step === "identifier" && (
            <Tabs
              value={channel}
              onChange={(_, v: AuthChannel) => {
                if (v === "SMS" && !capabilities.smsLoginEnabled) return;
                setChannel(v);
                setError(null);
                setInfo(null);
              }}
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab value="EMAIL" label="E-posta" />
              <Tab
                value="SMS"
                disabled={!capabilities.smsLoginEnabled}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>SMS</span>
                    {!capabilities.smsLoginEnabled && <Chip size="small" label="Yakında" />}
                  </Stack>
                }
              />
            </Tabs>
          )}

          {info && step === "code" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {info}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === "select" ? (
            <Stack spacing={1}>
              {candidates.map((c) => (
                <Button
                  key={c.recordId}
                  fullWidth
                  variant="outlined"
                  onClick={() => chooseSchool(c.recordId)}
                  disabled={loading}
                  startIcon={<SchoolOutlined />}
                  sx={{ justifyContent: "flex-start", textAlign: "left", py: 1.25 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Typography variant="body2" fontWeight={600}>
                      {c.schoolName ?? "Okul belirtilmemiş"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.role}
                    </Typography>
                  </Box>
                </Button>
              ))}
              <Button variant="text" onClick={resetToIdentifier} disabled={loading} sx={{ mt: 1 }}>
                Farklı hesap ile giriş
              </Button>
            </Stack>
          ) : step === "identifier" ? (
            <Box component="form" onSubmit={requestOtp}>
              {channel === "EMAIL" ? (
                <TextField
                  fullWidth
                  size="medium"
                  type="email"
                  label="E-posta"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                  InputProps={{ startAdornment: <MailOutline sx={{ mr: 1, color: "text.secondary" }} /> }}
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
              ) : (
                <PhoneField
                  fullWidth
                  size="medium"
                  label="Cep Telefonu"
                  value={phone}
                  onChange={setPhone}
                  autoFocus
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
              )}
              <Button
                fullWidth
                size="large"
                variant="contained"
                type="submit"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                Doğrulama Kodu Gönder
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={login}>
              <TextField
                fullWidth
                size="medium"
                label="Doğrulama Kodu"
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(val);
                  if (val.length === 6) login({ preventDefault: () => {} } as FormEvent, val);
                }}
                placeholder="••••••"
                inputMode="numeric"
                inputRef={codeInputRef}
                InputProps={{ startAdornment: <LockOutlined sx={{ mr: 1, color: "text.secondary" }} /> }}
                sx={{ mb: 2, "& input": { letterSpacing: "0.4em", textAlign: "center", fontSize: 22 } }}
                disabled={loading}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="text" onClick={resetToIdentifier} disabled={loading}>
                  {channel === "EMAIL" ? "E-postayı değiştir" : "Telefonu değiştir"}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="large"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  Giriş Yap
                </Button>
              </Stack>
            </Box>
          )}

          {step === "identifier" && (
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                Okulunuz henüz kayıtlı değil mi?{" "}
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => setRequestOpen(true)}
                sx={{ verticalAlign: "baseline", px: 0.5, minWidth: 0 }}
              >
                Kayıt için başvurun
              </Button>
            </Box>
          )}

          {isDevLoginEnabled() && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">Hızlı Giriş</Typography>
              </Divider>
              <Stack direction="row" spacing={1} justifyContent="center">
                {DEV_USERS.map((u) => (
                  <Chip
                    key={u.email}
                    icon={<u.icon />}
                    label={u.label}
                    color={u.color}
                    variant="outlined"
                    onClick={() => quickLogin(u.email)}
                    disabled={loading}
                    clickable
                  />
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <SchoolRegistrationRequestDialog open={requestOpen} onClose={() => setRequestOpen(false)} />
    </Box>
  );
};
